/**
 * Every view kind monday offers that we have **not** built yet (`specs/M4-V1-views.md` §5).
 *
 * They are registered, not omitted, for three reasons: the add-view gallery's shape never changes
 * when one lands; an imported view keeps its real `BoardView.type` string instead of degrading to
 * `null`; and opening one renders a *coming soon* screen naming the module rather than an error or
 * — worse — a table view pretending to be a gantt.
 *
 * Nothing here invents a UI. `settingsSchema` is a passthrough so an imported view's settings
 * survive a round trip untouched.
 */
import { z } from 'zod';
import { defineViewType } from '../types.js';
const passthrough = z.looseObject({});
function placeholder(input) {
    return defineViewType({
        id: input.id,
        title: input.title,
        mondayViewType: input.mondayViewType,
        kind: input.kind,
        settingsSchema: passthrough,
        defaultSettings: {},
        requiredColumnTypes: [],
        supportsFilters: input.supportsFilters ?? true,
        supportsSort: input.supportsSort ?? true,
        supportsGroupBy: input.supportsGroupBy ?? false,
        canBeDefault: input.canBeDefault ?? true,
        implemented: false,
        plannedIn: input.plannedIn,
        gallery: { order: input.order, section: input.section ?? 'board-views' },
    });
}
/**
 * monday's gallery order, measured: Table · Gantt · Chart · Calendar · Kanban · Canvas · Doc ·
 * File gallery · Form · Dashboard, then *Explore more views* holds Timeline · Workload · Map
 * `[cap:sandbox-add-view-menu.png]`. Canvas is monday's own AI surface and is deliberately absent
 * (`specs/M1-B1` §12 B1-12); `table` (order 1) and `form` (order 9) are registered by their own
 * folders because they are built.
 */
/* `gantt` (order 2) is no longer a placeholder: M4.V3 built it (`views/gantt/index.ts`), and its
   real `BoardView.type` is **`TimelineGanttBoardView`** on all 8 production rows — the guessed
   `GanttBoardView` this line used appears nowhere in the account (`specs/M4-V3-gantt.md` §2.5). */
export const chartView = placeholder({ id: 'chart', title: 'Chart', mondayViewType: 'ChartBoardView', kind: 'DASHBOARD', plannedIn: 'M4.V6', order: 3, supportsGroupBy: true });
/* `calendar` (order 4) and `kanban` (order 5) are no longer placeholders: M4.V4 and M4.V2
   built them (`views/calendar/index.ts`, `views/kanban/index.ts`). */
export const docView = placeholder({ id: 'doc', title: 'Doc', mondayViewType: 'DocBoardView', kind: 'APP', plannedIn: 'M11', order: 6, supportsFilters: false, supportsSort: false, canBeDefault: false });
/**
 * monday's **File gallery** is a widget over the board's files, not the **Cards** view that
 * M4.V7 built (`ItemsGalleryBoardView`, `views/gallery/index.ts`). Conflating the two would
 * make an imported file gallery render a card grid, so this keeps its own placeholder and its
 * `plannedIn` moves from `M4.V7` to `M4.V9` (`specs/M4-V7-gallery.md` §8).
 */
export const filesView = placeholder({ id: 'files', title: 'File gallery', mondayViewType: 'FilesGalleryBoardView', kind: 'APP', plannedIn: 'M4.V9', order: 7 });
/* `dashboard` is no longer a placeholder: M7.D1 built it (`views/dashboard/index.ts`). */
/* `timeline` (11) and `workload` (12) are no longer placeholders either: M4.V5 and M4.V6 built
   them (`views/timeline/index.ts`, `views/workload/index.ts`). */
export const mapView = placeholder({ id: 'map', title: 'Map', mondayViewType: 'MapBoardView', kind: 'APP', plannedIn: 'M4.V9', order: 13, section: 'explore' });
/**
 * A board view rendered by an app rather than a core kind — 46 of them on this account, almost
 * all monday's own empty "vibe" views `[vui]`. It is registered so `FeatureBoardView` resolves to
 * something; the host itself is M10.
 */
export const appView = placeholder({ id: 'app', title: 'App view', mondayViewType: 'FeatureBoardView', kind: 'APP', plannedIn: 'M10', order: 14, section: 'explore', supportsFilters: false, supportsSort: false, canBeDefault: false });
export const PLACEHOLDER_VIEW_TYPES = [
    chartView, docView, filesView, mapView, appView,
];
//# sourceMappingURL=index.js.map